import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';

const Services = () => {
  const [animationKey, setAnimationKey] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimationKey(prev => prev + 1);
          }
        });
      },
      { threshold: 0.3 }
    );

    const section = document.getElementById('services');
    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  return (
    <section id="services" className="pt-8 sm:pt-12 lg:pt-16 pb-14 sm:pb-20 lg:pb-24 bg-cream">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">
        <motion.div
          key={`services-header-${animationKey}`}
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-10 sm:mb-12 lg:mb-14"
        >
          <div className="text-navy text-xs sm:text-sm font-serif tracking-wider mb-2 sm:mb-3">OUR SERVICES</div>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold mb-3 sm:mb-4 px-4">
            <span className="text-navy">Diversified Excellence in </span>
            <br className="hidden sm:block" />
            <span className="text-gold">Property, Healthcare &amp; Investments</span>
          </h2>
          <div className="max-w-2xl mx-auto mt-4 sm:mt-5 px-4">
            <p className="text-text-light text-sm sm:text-base leading-relaxed">
              Our comprehensive investment approach spans three core sectors, each carefully selected for their resilience, growth potential, and ability to preserve and enhance family wealth across generations.
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6">
          {/* Large Left Card - Property */}
          <motion.div
            key={`services-property-${animationKey}`}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative lg:row-span-2 rounded-2xl overflow-hidden group cursor-pointer h-[420px] sm:h-[480px] lg:h-[520px] shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
          >
            <img
              src="https://public.readdy.ai/ai/img_res/7f5e93576099b5a21b30c0c34c448faf.jpg"
              alt="Property Investments"
              className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/50 to-transparent group-hover:from-navy/90 group-hover:via-navy/60 transition-all duration-500"></div>
            <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 lg:p-8 transform group-hover:translate-y-[-8px] transition-transform duration-500">
              <div className="inline-block bg-navy/80 backdrop-blur-sm px-4 sm:px-5 py-2 rounded-full border-l-4 border-gold mb-2 sm:mb-3 group-hover:bg-gold group-hover:border-navy transition-all duration-500">
                <span className="text-white font-semibold text-sm sm:text-base group-hover:text-navy transition-colors duration-500">PROPERTY INVESTMENTS</span>
              </div>
              <p className="text-white/90 text-xs sm:text-sm leading-relaxed max-w-md opacity-90 group-hover:opacity-100 transition-opacity duration-500">
                Prime residential and commercial properties across London and the UK, carefully selected for capital appreciation and rental yield optimization.
              </p>
              <div className="mt-3 flex items-center text-gold opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-500">
                <span className="text-xs font-semibold mr-2">Learn More</span>
                <i className="ri-arrow-right-line text-sm"></i>
              </div>
            </div>
          </motion.div>

          {/* Top Right Card - Healthcare */}
          <motion.div
            key={`services-healthcare-${animationKey}`}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative rounded-2xl overflow-hidden group cursor-pointer h-[200px] sm:h-[230px] lg:h-[252px] shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
          >
            <img
              src="https://readdy.ai/api/search-image?query=modern%20healthcare%20medical%20facility%20investment%20sophisticated%20hospital%20interior%20with%20advanced%20technology%20clean%20professional%20environment%20featuring%20contemporary%20medical%20equipment%20and%20elegant%20design%20healthcare%20innovation%20and%20excellence%20with%20cool%20blue%20tones%20and%20simple%20minimalist%20background&width=800&height=600&seq=service002&orientation=landscape"
              alt="Healthcare Ventures"
              className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/50 to-transparent group-hover:from-navy/90 group-hover:via-navy/60 transition-all duration-500"></div>
            <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 transform group-hover:translate-y-[-6px] transition-transform duration-500">
              <div className="inline-block bg-navy/80 backdrop-blur-sm px-4 sm:px-5 py-2 rounded-full border-l-4 border-gold mb-2 group-hover:bg-gold group-hover:border-navy transition-all duration-500">
                <span className="text-white font-semibold text-xs sm:text-sm group-hover:text-navy transition-colors duration-500">HEALTHCARE VENTURES</span>
              </div>
              <p className="text-white/90 text-xs leading-relaxed opacity-90 group-hover:opacity-100 transition-opacity duration-500">
                Strategic investments in private healthcare facilities, medical technology, and wellness sectors with strong growth potential.
              </p>
              <div className="mt-2 flex items-center text-gold opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-500">
                <span className="text-xs font-semibold mr-2">Learn More</span>
                <i className="ri-arrow-right-line text-sm"></i>
              </div>
            </div>
          </motion.div>

          {/* Bottom Right Card - Strategic Investments */}
          <motion.div
            key={`services-investments-${animationKey}`}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="relative rounded-2xl overflow-hidden group cursor-pointer h-[200px] sm:h-[230px] lg:h-[252px] shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
          >
            <img
              src="https://readdy.ai/api/search-image?query=sophisticated%20financial%20investment%20strategy%20elegant%20modern%20office%20environment%20with%20professional%20business%20atmosphere%20featuring%20refined%20interior%20design%20warm%20ambient%20lighting%20and%20premium%20furnishings%20representing%20strategic%20wealth%20management%20and%20portfolio%20diversification%20with%20simple%20clean%20background&width=800&height=600&seq=service003&orientation=landscape"
              alt="Strategic Investments"
              className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/50 to-transparent group-hover:from-navy/90 group-hover:via-navy/60 transition-all duration-500"></div>
            <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 transform group-hover:translate-y-[-6px] transition-transform duration-500">
              <div className="inline-block bg-navy/80 backdrop-blur-sm px-4 sm:px-5 py-2 rounded-full border-l-4 border-gold mb-2 group-hover:bg-gold group-hover:border-navy transition-all duration-500">
                <span className="text-white font-semibold text-xs sm:text-sm group-hover:text-navy transition-colors duration-500">STRATEGIC INVESTMENTS</span>
              </div>
              <p className="text-white/90 text-xs leading-relaxed opacity-90 group-hover:opacity-100 transition-opacity duration-500">
                Diversified portfolio management including equities, bonds, alternative assets, and private equity opportunities.
              </p>
              <div className="mt-2 flex items-center text-gold opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-500">
                <span className="text-xs font-semibold mr-2">Learn More</span>
                <i className="ri-arrow-right-line text-sm"></i>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Service Details */}
        <motion.div
          key={`services-details-${animationKey}`}
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-8 sm:mt-10 lg:mt-12 grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6"
        >
          <div className="text-center group cursor-pointer">
            <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-gold/10 rounded-xl flex items-center justify-center mx-auto mb-2 sm:mb-3 group-hover:bg-gold/20 group-hover:scale-110 transition-all duration-300">
              <i className="ri-building-line text-gold text-xl sm:text-2xl group-hover:scale-110 transition-transform duration-300"></i>
            </div>
            <h4 className="text-navy font-bold text-sm sm:text-base mb-1 px-2 group-hover:text-gold transition-colors duration-300">Property Portfolio</h4>
            <p className="text-text-light text-xs">100+ premium properties</p>
          </div>
          <div className="text-center group cursor-pointer">
            <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-gold/10 rounded-xl flex items-center justify-center mx-auto mb-2 sm:mb-3 group-hover:bg-gold/20 group-hover:scale-110 transition-all duration-300">
              <i className="ri-heart-pulse-line text-gold text-xl sm:text-2xl group-hover:scale-110 transition-transform duration-300"></i>
            </div>
            <h4 className="text-navy font-bold text-sm sm:text-base mb-1 px-2 group-hover:text-gold transition-colors duration-300">Healthcare Assets</h4>
            <p className="text-text-light text-xs">12 medical facilities</p>
          </div>
          <div className="text-center group cursor-pointer">
            <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-gold/10 rounded-xl flex items-center justify-center mx-auto mb-2 sm:mb-3 group-hover:bg-gold/20 group-hover:scale-110 transition-all duration-300">
              <i className="ri-funds-line text-gold text-xl sm:text-2xl group-hover:scale-110 transition-transform duration-300"></i>
            </div>
            <h4 className="text-navy font-bold text-sm sm:text-base mb-1 px-2 group-hover:text-gold transition-colors duration-300">Investment Funds</h4>
            <p className="text-text-light text-xs">£20M+ diversified</p>
          </div>
          <div className="text-center group cursor-pointer">
            <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-gold/10 rounded-xl flex items-center justify-center mx-auto mb-2 sm:mb-3 group-hover:bg-gold/20 group-hover:scale-110 transition-all duration-300">
              <i className="ri-global-line text-gold text-xl sm:text-2xl group-hover:scale-110 transition-transform duration-300"></i>
            </div>
            <h4 className="text-navy font-bold text-sm sm:text-base mb-1 px-2 group-hover:text-gold transition-colors duration-300">Global Reach</h4>
            <p className="text-text-light text-xs">UK &amp; International</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
